import { useMemo } from 'react'
import { LojaAgregador } from '../../lojas/type'
import { AggregatorPublicity } from '../../publicidade/agregador-publicity.type'
import { AggregatorClassification } from '../../AggregadorCategory/aggregadorCategory.type'
import { DinamicPage } from '../../dinamicPage/dinamyc-pages.types'
import { Post } from '../../feedRecursos'

interface AggregatorStats {
  activeStoresCount: number
  inactiveStoresCount: number
  activePublicityCount: number
  inactivePublicityCount: number
  expiredPublicityCount: number
  expiringPublicityCount: number
  totalPublicityCount: number
  activePagesCount: number
  inactivePagesCount: number
  totalPagesCount: number
  activeClassificationsCount: number
  inactiveClassificationsCount: number
  totalClassificationsCount: number
  activePostsCount: number
  inactivePostsCount: number
  totalPostsCount: number
  expiringPostsCount: number
}

interface PublicityByStoreData {
  store: string
  count: number
}

interface PublicityByTypeData {
  type: string
  count: number
}

interface ExpiringPublicityData {
  title: string
  daysUntilExpiry: number
  endDate: string
}

export function useDashboardStats(
  lojasAgregador: LojaAgregador[],
  aggregatorPublicity: AggregatorPublicity[],
  aggregatorCategories: AggregatorClassification[],
  aggregatorDinamicPage: DinamicPage[],
  posts: Post[] = []
) {
  // Count active/inactive stores
  const getStoresStats = useMemo(() => {
    return {
      activeStoresCount: lojasAgregador.filter((s) => s.active).length,
      inactiveStoresCount: lojasAgregador.filter((s) => !s.active).length,
      totalStoresCount: lojasAgregador.length,
    }
  }, [lojasAgregador])

  // Count publicity stats
  const getPublicityStats = useMemo(() => {
    const now = new Date()

    const activePublicity = aggregatorPublicity.filter(
      (p) => p.status && new Date(p.endDate) > now
    )
    const expiredPublicity = aggregatorPublicity.filter(
      (p) => new Date(p.endDate) < now
    )
    const inactivePublicity = aggregatorPublicity.filter((p) => !p.status)

    // Publicities expiring within 30 days
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    const expiringPublicity = aggregatorPublicity.filter(
      (p) =>
        p.status &&
        new Date(p.endDate) <= thirtyDaysFromNow &&
        new Date(p.endDate) > now
    )

    return {
      activePublicityCount: activePublicity.length,
      inactivePublicityCount: inactivePublicity.length,
      expiredPublicityCount: expiredPublicity.length,
      expiringPublicityCount: expiringPublicity.length,
      totalPublicityCount: aggregatorPublicity.length,
    }
  }, [aggregatorPublicity])

  // Count classifications stats
  const getClassificationsStats = useMemo(() => {
    return {
      activeClassificationsCount: aggregatorCategories.filter(
        (c) => c.status
      ).length,
      inactiveClassificationsCount: aggregatorCategories.filter(
        (c) => !c.status
      ).length,
      totalClassificationsCount: aggregatorCategories.length,
    }
  }, [aggregatorCategories])

  // Count pages stats
  const getPagesStats = useMemo(() => {
    return {
      activePagesCount: aggregatorDinamicPage.filter((p) => p.status).length,
      inactivePagesCount: aggregatorDinamicPage.filter((p) => !p.status).length,
      totalPagesCount: aggregatorDinamicPage.length,
    }
  }, [aggregatorDinamicPage])

  // Count posts stats
  const getPostsStats = useMemo(() => {
    const now = new Date()

    const activePosts = posts.filter((p) => p.status)
    const inactivePosts = posts.filter((p) => !p.status)

    // Posts expiring within 30 days
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    const expiringPosts = posts.filter(
      (p) =>
        p.status &&
        p.dateFin &&
        new Date(p.dateFin) > now &&
        new Date(p.dateFin) <= thirtyDaysFromNow
    )

    return {
      activePostsCount: activePosts.length,
      inactivePostsCount: inactivePosts.length,
      totalPostsCount: posts.length,
      expiringPostsCount: expiringPosts.length,
    }
  }, [posts])

  // Get store status distribution
  const getStoreStatusDistribution = useMemo(() => {
    return [
      {
        name: 'Ativas',
        value: getStoresStats.activeStoresCount,
      },
      {
        name: 'Inativas',
        value: getStoresStats.inactiveStoresCount,
      },
    ]
  }, [getStoresStats])

  // Get publicity status distribution
  const getPublicityStatusDistribution = useMemo(() => {
    return [
      {
        name: 'Ativas',
        value: getPublicityStats.activePublicityCount,
      },
      {
        name: 'Expiradas',
        value: getPublicityStats.expiredPublicityCount,
      },
      {
        name: 'Inativas',
        value: getPublicityStats.inactivePublicityCount,
      },
    ]
  }, [getPublicityStats])

  // Get stores count by classification
  const getStoresByClassification = useMemo(() => {
    const classificationMap = new Map<string, number>()

    lojasAgregador.forEach((store) => {
      const classId = store.classificationId || 'Sem Categoria'
      const category = aggregatorCategories.find(
        (c) => c.id.toString() === classId.toString()
      )
      const categoryName = category?.name || 'Sem Categoria'

      classificationMap.set(
        categoryName,
        (classificationMap.get(categoryName) || 0) + 1
      )
    })

    return Array.from(classificationMap, ([name, count]) => ({
      category: name,
      count,
    }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [lojasAgregador, aggregatorCategories])

  // Get publicity count by type (banner/highlight)
  const getPublicityByType = useMemo(() => {
    const typeMap = new Map<string, number>()

    aggregatorPublicity.forEach((p) => {
      const type = p.type || 'Outros'
      typeMap.set(type, (typeMap.get(type) || 0) + 1)
    })

    return Array.from(typeMap, ([type, count]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      count,
    }))
  }, [aggregatorPublicity])

  // Get publicity count by store (top 10)
  const getPublicityByStore = useMemo(() => {
    const storeMap = new Map<string, number>()

    aggregatorPublicity.forEach((p) => {
      const store = lojasAgregador.find(
        (s) => s.pluginspaceId === p.pluginspaceId
      )
      const storeName = store?.name || `Loja #${p.pluginspaceId}`

      storeMap.set(storeName, (storeMap.get(storeName) || 0) + 1)
    })

    return Array.from(storeMap, ([store, count]) => ({
      store,
      count,
    }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [aggregatorPublicity, lojasAgregador])

  // Get publicity timeline data (created per month)
  const getPublicityTimeline = useMemo(() => {
    const monthMap = new Map<string, number>()

    aggregatorPublicity.forEach((p) => {
      if (p.created) {
        const date = new Date(p.created)
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, '0')}`
        monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + 1)
      }
    })

    return Array.from(monthMap, ([month, count]) => ({
      month,
      count,
    }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12) // Last 12 months
  }, [aggregatorPublicity])

  // Get expiring publicity (next 30 days)
  const getExpiringPublicity = useMemo(() => {
    const now = new Date()
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)

    return aggregatorPublicity
      .filter(
        (p) =>
          p.status &&
          new Date(p.endDate) > now &&
          new Date(p.endDate) <= thirtyDaysFromNow
      )
      .map((p) => {
        const daysUntilExpiry = Math.ceil(
          (new Date(p.endDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        )
        return {
          title: p.title,
          daysUntilExpiry,
          endDate: p.endDate,
        }
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
      .slice(0, 10)
  }, [aggregatorPublicity])

  // Get post status distribution
  const getPostStatusDistribution = useMemo(() => {
    return [
      {
        name: 'Ativas',
        value: getPostsStats.activePostsCount,
      },
      {
        name: 'Inativas',
        value: getPostsStats.inactivePostsCount,
      },
    ]
  }, [getPostsStats])

  // Get expiring posts (next 30 days)
  const getExpiringPosts = useMemo(() => {
    const now = new Date()
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)

    return posts
      .filter(
        (p) =>
          p.status &&
          p.dateFin &&
          new Date(p.dateFin) > now &&
          new Date(p.dateFin) <= thirtyDaysFromNow
      )
      .map((p) => {
        const daysUntilExpiry = Math.ceil(
          (new Date(p.dateFin).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        )
        return {
          title: p.title,
          daysUntilExpiry,
          endDate: p.dateFin,
        }
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
      .slice(0, 10)
  }, [posts])

  // Aggregate all stats
  const aggregatorStats: AggregatorStats = useMemo(
    () => ({
      ...getStoresStats,
      ...getPublicityStats,
      ...getClassificationsStats,
      ...getPagesStats,
      ...getPostsStats,
    }),
    [
      getStoresStats,
      getPublicityStats,
      getClassificationsStats,
      getPagesStats,
      getPostsStats,
    ]
  )

  return {
    stats: aggregatorStats,
    getStoreStatusDistribution,
    getPublicityStatusDistribution,
    getPostStatusDistribution,
    getStoresByClassification,
    getPublicityByType,
    getPublicityByStore,
    getPublicityTimeline,
    getExpiringPublicity,
    getExpiringPosts,
    storesStats: getStoresStats,
    publicityStats: getPublicityStats,
    classificationsStats: getClassificationsStats,
    pagesStats: getPagesStats,
    postsStats: getPostsStats,
  }
}
